import api from "../api";

const complaintsService = {
  // Get all complaints with optional filtering and pagination
  getComplaints: async (filters = {}, page = 1, limit = 50) => {
    try {
      const queryParams = new URLSearchParams();

      // Add pagination params
      queryParams.append("page", page);
      queryParams.append("limit", limit);

      // Add filter params
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "") {
          queryParams.append(key, value);
        }
      });

      console.log("🔍 [SERVICE] Making API call to /complaints with params:", queryParams.toString());
      const response = await api.get(`/complaints?${queryParams}`);
      console.log("🔍 [SERVICE] API response:", response.data);

      // Backend returns { data: [...], pagination: {...} }
      if (response.data && response.data.data) {
        console.log(
          "🔍 [SERVICE] Returning data with pagination:",
          response.data.data.length,
          "items, total:",
          response.data.pagination?.total
        );
        return {
          data: response.data.data,
          pagination: response.data.pagination || {
            current_page: page,
            per_page: limit,
            total: response.data.data.length,
            last_page: 1
          }
        };
      } else if (Array.isArray(response.data)) {
        console.log(
          "🔍 [SERVICE] Returning direct array:",
          response.data.length
        );
        return {
          data: response.data,
          pagination: {
            current_page: page,
            per_page: limit,
            total: response.data.length,
            last_page: 1
          }
        };
      } else {
        console.log("🔍 [SERVICE] No data found, returning empty result");
        return {
          data: [],
          pagination: {
            current_page: 1,
            per_page: limit,
            total: 0,
            last_page: 1
          }
        };
      }
    } catch (error) {
      console.error("Error fetching complaints:", error);
      return {
        data: [],
        pagination: {
          current_page: 1,
          per_page: limit,
          total: 0,
          last_page: 1
        }
      };
    }
  },

  // Get complaint by ID
  getComplaintById: async (id) => {
    try {
      const response = await api.get(`/complaints/${id}`);
      // Backend returns the complaint object directly
      return response.data;
    } catch (error) {
      console.error("Error fetching complaint:", error);
      throw error;
    }
  },

  // Create new complaint
  createComplaint: async (complaintData) => {
    try {
      const response = await api.post("/complaints", complaintData);
      // Backend returns the created complaint object directly
      return response.data;
    } catch (error) {
      console.error("Error creating complaint:", error);
      throw error;
    }
  },

  // Update complaint
  updateComplaint: async (id, updateData) => {
    try {
      const response = await api.put(`/complaints/${id}`, updateData);
      // Backend returns the updated complaint object directly
      return response.data;
    } catch (error) {
      console.error("Error updating complaint:", error);
      throw error;
    }
  },

  // Delete complaint
  deleteComplaint: async (id) => {
    try {
      const response = await api.delete(`/complaints/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting complaint:", error);
      throw error;
    }
  },

  // Bulk delete complaints
  bulkDeleteComplaints: async (complaintIds) => {
    try {
      const response = await api.post("/complaints/bulk-delete", {
        ids: complaintIds, // Backend expects 'ids' not 'complaintsToDelete'
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk deleting complaints:", error);
      throw error;
    }
  },

  // Get complaints status overview
  getStatusOverview: async () => {
    try {
      const response = await api.get("/complaints/status-overview");
      // Backend returns the overview object directly
      return response.data;
    } catch (error) {
      console.error("Error fetching complaints status overview:", error);
      return {};
    }
  },

  // Get filter data
  getFilterData: async () => {
    try {
      const response = await api.get("/complaints/filter-data");
      // Backend returns the filter data object directly
      return (
        response.data || {
          jobTypes: [],
          propertyAreas: [],
          clients: [],
          priorities: [],
          contractors: [],
          users: [], // Added for calendar dropdown
        }
      );
    } catch (error) {
      console.error("Error fetching filter data:", error);
      return {
        jobTypes: [],
        propertyAreas: [],
        clients: [],
        priorities: [],
        contractors: [],
        users: [], // Added for calendar dropdown
      };
    }
  },

  // Get or create a ticket for a complaint (for show/edit redirects)
  getComplaintTicket: async (complaintId) => {
    try {
      const response = await api.get(`/complaints/${complaintId}/ticket`);
      return response.data;
    } catch (error) {
      console.error("Error getting complaint ticket:", error);
      throw error;
    }
  },
};

export default complaintsService;
